import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import { useDevice } from '@/hooks';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { Button } from '@/components/common/Button/Button';
import { formatCoordinate, formatTimeAgo } from '@/lib/formatters';
import { env } from '@/config/env';
import type { AccountDeviceRespObj } from '@/api/models';

/** Automatically adjusts map viewport to fit all markers. */
function AutoFitBounds({
  devices,
}: {
  devices: AccountDeviceRespObj[];
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || devices.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    devices.forEach((d) => {
      bounds.extend({
        lat: d.gpsLocationData.latitude,
        lng: d.gpsLocationData.longitude,
      });
    });

    if (devices.length === 1) {
      // Pad single-point bounds so we don't zoom in too far
      const center = bounds.getCenter();
      bounds.extend({ lat: center.lat() + 0.5, lng: center.lng() + 0.5 });
      bounds.extend({ lat: center.lat() - 0.5, lng: center.lng() - 0.5 });
    }

    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, [map, devices]);

  return null;
}

function DeviceMarker({
  device,
  onSelect,
}: {
  device: AccountDeviceRespObj;
  onSelect: () => void;
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const { yachtName, deviceProperties, gpsLocationData } = device;
  if (!gpsLocationData?.locationValid) return null;

  const name = yachtName?.yachtName || 'Unnamed Vessel';
  const isConnected =
    deviceProperties?.isDirectConnected || deviceProperties?.isIoTCoreConnected;

  const position = {
    lat: gpsLocationData.latitude,
    lng: gpsLocationData.longitude,
  };

  return (
    <>
      <AdvancedMarker position={position} onClick={() => setInfoOpen(true)}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: isConnected ? '#06B6D4' : '#94A3B8',
            border: '3px solid #fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#fff',
            }}
          />
        </div>
      </AdvancedMarker>

      {infoOpen && (
        <InfoWindow position={position} onCloseClick={() => setInfoOpen(false)}>
          <div className="min-w-[180px]">
            <h3 className="text-sm font-semibold text-slate-900">{name}</h3>
            <p className="mt-1 text-xs text-slate-500">
              {formatCoordinate(gpsLocationData.latitude, gpsLocationData.longitude)}
            </p>
            {gpsLocationData.updatedDT && (
              <p className="text-xs text-slate-400">
                Updated {formatTimeAgo(gpsLocationData.updatedDT)}
              </p>
            )}
            {gpsLocationData.speed != null && gpsLocationData.speed > 0 && (
              <p className="text-xs text-slate-500">
                Speed: {gpsLocationData.speed.toFixed(1)} kts
              </p>
            )}
            <button
              onClick={onSelect}
              className="mt-2 rounded bg-teal-500 px-3 py-1 text-xs font-medium text-white hover:bg-teal-600"
            >
              View Details
            </button>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

export function FleetMapPage() {
  const { devices, devicesLoading, selectDevice } = useDevice();
  const navigate = useNavigate();

  const mappableDevices = useMemo(
    () => devices.filter((d) => d.gpsLocationData?.locationValid),
    [devices],
  );

  const defaultCenter = useMemo(() => {
    if (mappableDevices.length === 0) return { lat: 25.7617, lng: -80.1918 };
    const first = mappableDevices[0].gpsLocationData;
    return { lat: first.latitude, lng: first.longitude };
  }, [mappableDevices]);

  const handleSelect = useCallback(
    (deviceUUID: string) => {
      selectDevice(deviceUUID);
      navigate(`/device/${deviceUUID}/tracking`);
    },
    [selectDevice, navigate],
  );

  if (devicesLoading && devices.length === 0) {
    return <PageSpinner />;
  }

  if (mappableDevices.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Fleet Map
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            View all your vessels on the map
          </p>
        </div>
        <EmptyState
          title="No GPS data available"
          description="None of your devices have reported a GPS position yet."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Fleet Map
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {mappableDevices.length} vessel
            {mappableDevices.length !== 1 ? 's' : ''} on map
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => navigate('/devices')}>
          List View
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-navy-700">
        <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
          <Map
            mapId="fleet-map"
            defaultCenter={defaultCenter}
            defaultZoom={10}
            style={{ height: 'calc(100vh - 200px)', width: '100%' }}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapTypeId="hybrid"
          >
            <AutoFitBounds devices={mappableDevices} />
            {mappableDevices.map((device) => (
              <DeviceMarker
                key={device.deviceProperties.deviceUUID}
                device={device}
                onSelect={() =>
                  handleSelect(device.deviceProperties.deviceUUID)
                }
              />
            ))}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
}
